import {Component, EventEmitter, inject, OnInit, Output, signal} from '@angular/core';
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {forkJoin} from 'rxjs';
import {environment} from "projects/store/src/environments/environment";
import {RequestService} from "projects/store/src/app/services/api/v1/request.service";
import {CustomMessageService, OriginMessageError, UtilService} from "library";
import {ActivatedRoute} from "@angular/router";
import {PathNavigationService} from 'projects/store/src/app/services/api/v1/path-navigation.service';

const ATTRIBUTES_CODE = 'claro_e_commerce_data';

interface Percentage {
  label: string;
  value: number;
}

@Component({
  selector: 'app-sales-select-conditions',
  templateUrl: './sales-select-conditions.component.html',
  styles: []
})
export class SalesSelectConditionsComponent implements OnInit {
  private readonly requestService = inject(RequestService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly utilService = inject(UtilService);
  private readonly route = inject(ActivatedRoute);
  private readonly pathNavigationService = inject(PathNavigationService);

  @Output() nextStepEvent = new EventEmitter();
  @Output() gotoStepEvent = new EventEmitter();

  requestId!: string;
  flowCode!: string;
  country = environment.global.country;

  product = signal<any>({});
  data = signal<any>({});

  // Precio (viene del endpoint de attributes - totalAmount)
  priceInputValue: number | null = null;
  originalPriceInputValue: number | null = null;
  attributesData: any = null;

  // Abono
  percentagesOnProduct: Percentage[] = [];
  selectedPercentagesOnProduct: any;

  // Frecuencia
  frequency: { label: string; value: any; }[] = [];
  frequencySelected: any;

  // Cuotas
  selectedQuote: any;
  showRadioSelectionBox: boolean = false;
  allProductQuotes: any[] = [];

  loading = false;

  ngOnInit() {
    this.data.set(this.requestService.request);
    this.requestId = this.route.snapshot.params['request_id'];
    this.flowCode = this.route.snapshot.params['flow_code'];

    this.loadProduct();
  }

  loadProduct() {
    this.utilService.enablePageLoading();
    const categoryId = this.requestService.categoryId;

    forkJoin({
      products: this.requestService.getProductsByCategory(this.requestId, categoryId),
      attributes: this.requestService.getRequestAttributes(this.requestId, ATTRIBUTES_CODE),
    }).subscribe({
      next: (resp) => {
        if (resp.attributes?.status === HttpStatusCode.Ok) {
          this.attributesData = resp.attributes.body.data.value;
          this.priceInputValue = this.attributesData.totalAmount;
          this.originalPriceInputValue = this.attributesData.totalAmount;
        }

        if (resp.products?.status === HttpStatusCode.Ok) {
          const products = resp.products.body.data.list;
          if (products.length > 0) {
            this.initProduct(products[0]);
          }
        } else {
          this.customMessageService.responseNoticeHandler(resp.products, OriginMessageError.BACKEND);
        }

        this.utilService.disablePageLoading();
      },
      error: (err: HttpErrorResponse) => {
        this.customMessageService.responseNoticeHandler(err, OriginMessageError.BACKEND);
        this.utilService.disablePageLoading();
      }
    });
  }

  initProduct(product: any) {
    product.quotes.sort((a: any, b: any) => a.quota.quantity - b.quota.quantity);
    this.product.set(product);
    this.onCreateArrayPercentages(product);
    this.setPaymentFrequency(product.payment_frequency);
    this.selectedPercentagesOnProduct = this.percentagesOnProduct[0].value;
    this.onPercentageChange();
  }

  // --- Precio ---

  onCalculateProductPrice(): number | null {
    return this.priceInputValue;
  }

  // --- Abono ---

  onCreateArrayPercentages(product: any) {
    const minInstallment = product.quotes[0]?.installment?.min;
    const maxInstallment = product.quotes[0]?.installment?.max;

    if (minInstallment == null || maxInstallment == null) {
      return;
    }

    this.percentagesOnProduct = [];
    for (let i: number = minInstallment; i <= maxInstallment; i += 5) {
      this.percentagesOnProduct.push({
        label: `${i}%`,
        value: i
      });
    }

    this.selectedPercentagesOnProduct = this.percentagesOnProduct[0].value;
  }

  onCalculateAmount(withDecimals: boolean): number {
    const productPrice = this.onCalculateProductPrice();
    if (!productPrice) {
      return 0;
    }
    const amount = (productPrice * Number(this.selectedPercentagesOnProduct) / 100);
    if (withDecimals) {
      return amount;
    } else {
      return Number(Math.floor(Number(amount.toFixed(2))).toFixed(2));
    }
  }

  // --- Frecuencia ---

  setPaymentFrequency(frequency: any) {
    this.frequency = frequency.map((f: any) => ({
      label: `${f} dias`,
      value: f
    }));
    this.frequency.sort((a: any, b: any) => a.quantity - b.quantity);
    this.frequencySelected = this.frequency[0].value ?? 15;
  }

  // --- Cuotas ---

  onPercentageChange() {
    const data = {
      price: this.onCalculateProductPrice(),
      installment: this.onCalculateAmount(true),
      quotas: this.product().quotes
        .filter((q: any) => q.frecuency === this.frequencySelected)
        .map((q: any) => ({
          quantity: q.quota.quantity,
          name: q.quota.name,
          monthly_interest: q.monthly_interest,
        })),
      payment_frequency: this.frequencySelected
    };
    this.showRadioSelectionBox = false;
    this.requestService.getCalculationCreditInstallments(data, this.requestId).subscribe({
      next: (result: any) => {
        if (result.status === HttpStatusCode.Ok) {
          this.calculateNewPaymentInstallment(result.body.data.list);
        } else {
          this.customMessageService.responseNoticeHandler(result, OriginMessageError.BACKEND);
        }
        this.utilService.disablePageLoading();
      },
      error: (err: any) => {
        this.customMessageService.responseNoticeHandler(err, OriginMessageError.BACKEND);
        this.utilService.disablePageLoading();
      }
    });
  }

  calculateNewPaymentInstallment(quotes: any[]) {
    const productCopy = structuredClone(this.product());
    const allQuotes = [...productCopy.quotes];

    let filteredIndex = 0;
    productCopy.quotes.forEach((item: any) => {
      if (item.frecuency === this.frequencySelected) {
        const updateValues = quotes[filteredIndex];
        if (updateValues) {
          item.quota.quantity = updateValues.quantity;
          item.quota.from = updateValues.payment;
        }
        filteredIndex++;
      }
    });

    productCopy.quotes = productCopy.quotes.filter(
      (item: any) => item.frecuency === this.frequencySelected
    );

    this.product.set(productCopy);
    this.allProductQuotes = allQuotes;
    this.showRadioSelectionBox = true;
  }

  // --- Guardar ---

  saveCalculateNewPaymentInstallment() {
    this.selectedQuote.installment.selected = this.onCalculateAmount(false);
    this.selectedQuote.prices.selected = this.onCalculateProductPrice();
    this.product().payment_frequency = this.frequencySelected;
    this.selectedQuote.frecuency = this.frequencySelected;
    this.selectedQuote.quota.selected = this.selectedQuote.quota.from;

    const data = {
      product: this.product(),
      quotes: this.selectedQuote
    };

    this.utilService.enablePageLoading();
    this.loading = true;
    this.requestService.update(this.requestId, data).subscribe({
      next: (resp) => {
        if (resp.status === HttpStatusCode.NoContent) {
          this.onNextStep();
        } else {
          this.customMessageService.responseNoticeHandler(resp, OriginMessageError.BACKEND);
        }
        this.utilService.disablePageLoading();
        this.loading = false;
      },
      error: (err: any) => {
        if (err.status === HttpStatusCode.Locked) {
          this.handleRequestLocked(err);
        } else {
          this.customMessageService.responseNoticeHandler(err, OriginMessageError.BACKEND);
          this.utilService.disablePageLoading();
          this.loading = false;
        }
      }
    });
  }

  onNextStep() {
    this.nextStepEvent.emit();
  }

  private handleRequestLocked(resp: any): void {
    const messageError = resp.error?.message ?? 'Solicitud no disponible';
    const attemptedUrl = this.utilService.getCurrentUrl();
    const state = { attemptedUrl, messageError };
    this.pathNavigationService.navigateToRequestNotAvailable(this.flowCode, state);
  }
}
